/**
 * IncidentAlertWidget Component
 * 
 * Dashboard widget showing open/critical incident counts with navigation to audit trail.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  XCircle,
  ChevronRight,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');
import { getIncidentSummary, invalidateIncidentCache } from '@/lib/offlineAudit';

/**
 * IncidentAlertWidget Component
 */
export function IncidentAlertWidget({ className }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch incident summary
  const fetchSummary = useCallback(async () => {
    try {
      const data = await getIncidentSummary();
      setSummary(data);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('[IncidentAlertWidget] Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and auto-refresh every 30 seconds
  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  // Navigate to incident audit trail
  const handleNavigate = (filter = null) => {
    if (filter) {
      navigate(`${bo('incident-audit')}?${new URLSearchParams(filter).toString()}`);
    } else {
      navigate(bo('incident-audit'));
    }
  };

  // Calculate totals
  const openCount = summary?.open || 0;
  const criticalCount = summary?.critical || 0;
  const failedCount = summary?.failed || 0;
  const conflictCount = summary?.conflict || 0;
  const needsAttention = criticalCount + failedCount + conflictCount;

  // Determine alert level
  const alertLevel = criticalCount > 0 ? 'critical' : needsAttention > 0 ? 'warning' : 'normal';

  // If no incidents and not loading, show minimal state
  if (!loading && openCount === 0 && needsAttention === 0) {
    return (
      <Card 
        className={cn(
          "bg-white border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow",
          className
        )}
        onClick={() => handleNavigate()}
        data-testid="incident-alert-widget"
      >
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-sm sm:text-base">All Systems Normal</p>
                <p className="text-xs text-gray-500">No open incidents</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={cn(
        "border-0 shadow-sm transition-all",
        alertLevel === 'critical' && "bg-red-50 border-l-4 border-l-red-500",
        alertLevel === 'warning' && "bg-amber-50 border-l-4 border-l-amber-500",
        alertLevel === 'normal' && "bg-white",
        className
      )}
      data-testid="incident-alert-widget"
    >
      <CardContent className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {alertLevel === 'critical' ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : alertLevel === 'warning' ? (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-blue-500" />
            )}
            <span className={cn(
              "font-semibold text-sm sm:text-base",
              alertLevel === 'critical' && "text-red-700",
              alertLevel === 'warning' && "text-amber-700",
              alertLevel === 'normal' && "text-gray-800"
            )}>
              {alertLevel === 'critical' ? 'Critical Incidents' : 
               alertLevel === 'warning' ? 'Incidents Need Attention' : 
               'Open Incidents'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              invalidateIncidentCache();
              fetchSummary();
            }}
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div 
            className="text-center p-2 rounded-lg bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
            onClick={() => handleNavigate({ unresolvedOnly: true })}
          >
            <p className="text-lg sm:text-xl font-bold text-blue-600">{openCount}</p>
            <p className="text-[10px] sm:text-xs text-gray-500">Open</p>
          </div>
          <div 
            className="text-center p-2 rounded-lg bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
            onClick={() => handleNavigate({ severity: 'critical' })}
          >
            <p className={cn(
              "text-lg sm:text-xl font-bold",
              criticalCount > 0 ? "text-red-600" : "text-gray-400"
            )}>
              {criticalCount}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">Critical</p>
          </div>
          <div 
            className="text-center p-2 rounded-lg bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
            onClick={() => handleNavigate({ status: 'failed' })}
          >
            <p className={cn(
              "text-lg sm:text-xl font-bold",
              failedCount > 0 ? "text-orange-600" : "text-gray-400"
            )}>
              {failedCount}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">Failed</p>
          </div>
          <div 
            className="text-center p-2 rounded-lg bg-white/50 cursor-pointer hover:bg-white/80 transition-colors"
            onClick={() => handleNavigate({ status: 'conflict' })}
          >
            <p className={cn(
              "text-lg sm:text-xl font-bold",
              conflictCount > 0 ? "text-purple-600" : "text-gray-400"
            )}>
              {conflictCount}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500">Conflicts</p>
          </div>
        </div>

        {/* Action Button */}
        <Button 
          variant={alertLevel === 'critical' ? 'destructive' : alertLevel === 'warning' ? 'default' : 'outline'}
          className="w-full h-9 text-sm"
          onClick={() => handleNavigate()}
        >
          View Incident Audit Trail
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>

        {/* Critical Alert Message */}
        {criticalCount > 0 && (
          <p className="text-xs text-red-600 mt-2 text-center">
            {criticalCount} critical incident{criticalCount !== 1 ? 's' : ''} require immediate attention
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default IncidentAlertWidget;
